/**
 * Render Sync System - syncs entity positions to their renderers
 */

import type { World, System } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { FactionComponent } from '@/game/components/Faction';
import type { UnitComponent } from '@/game/components/Unit';
import type { BuildingComponent } from '@/game/components/Building';
import type { UnitRenderer } from '@/rendering/units/UnitRenderer';
import type { BuildingRenderer } from '@/rendering/buildings/BuildingRenderer';
import type { CavalryRenderer } from '@/rendering/units/CavalryRenderer';
import type { ShieldBearerRenderer } from '@/rendering/units/ShieldBearerRenderer';
import type { MageRenderer } from '@/rendering/units/MageRenderer';
import type { ArcherRenderer } from '@/rendering/units/ArcherRenderer';
import type { SwordsmanRenderer } from '@/rendering/units/SwordsmanRenderer';
import type { SpearmanRenderer } from '@/rendering/units/SpearmanRenderer';
import type { Entity, UnitType, BuildingType, FactionId } from '@/types';

export class RenderSyncSystem implements System {
  private unitRenderer: UnitRenderer;
  private buildingRenderer: BuildingRenderer;
  private cavalryRenderer: CavalryRenderer | null = null;
  private shieldBearerRenderer: ShieldBearerRenderer | null = null;
  private mageRenderer: MageRenderer | null = null;
  private archerRenderer: ArcherRenderer | null = null;
  private swordsmanRenderer: SwordsmanRenderer | null = null;
  private spearmanRenderer: SpearmanRenderer | null = null;
  private trackedUnits: Set<Entity> = new Set();
  private trackedCavalry: Set<Entity> = new Set();
  private trackedShieldBearers: Set<Entity> = new Set();
  private trackedMages: Set<Entity> = new Set();
  private trackedArchers: Set<Entity> = new Set();
  private trackedSwordsmen: Set<Entity> = new Set();
  private trackedSpearmen: Set<Entity> = new Set();
  private trackedBuildings: Set<Entity> = new Set();

  constructor(unitRenderer: UnitRenderer, buildingRenderer: BuildingRenderer) {
    this.unitRenderer = unitRenderer;
    this.buildingRenderer = buildingRenderer;
  }

  setCavalryRenderer(cavalryRenderer: CavalryRenderer): void {
    this.cavalryRenderer = cavalryRenderer;
  }

  setShieldBearerRenderer(shieldBearerRenderer: ShieldBearerRenderer): void {
    this.shieldBearerRenderer = shieldBearerRenderer;
  }

  setMageRenderer(mageRenderer: MageRenderer): void {
    this.mageRenderer = mageRenderer;
  }

  setArcherRenderer(archerRenderer: ArcherRenderer): void {
    this.archerRenderer = archerRenderer;
  }

  setSwordsmanRenderer(swordsmanRenderer: SwordsmanRenderer): void {
    this.swordsmanRenderer = swordsmanRenderer;
  }

  setSpearmanRenderer(spearmanRenderer: SpearmanRenderer): void {
    this.spearmanRenderer = spearmanRenderer;
  }

  update(world: World, _deltaTime: number): void {
    // Sync units
    const units = world.query([ComponentTypes.Position, ComponentTypes.Faction, ComponentTypes.Unit]);
    const currentUnits = new Set(units);
    const currentCavalry = new Set<Entity>();
    const currentShieldBearers = new Set<Entity>();
    const currentMages = new Set<Entity>();
    const currentArchers = new Set<Entity>();
    const currentSwordsmen = new Set<Entity>();
    const currentSpearmen = new Set<Entity>();

    // Add new units
    for (const entity of units) {
      const unit = world.getComponent<UnitComponent>(entity, ComponentTypes.Unit)!;
      const isCavalry = unit.unitType === 'cavalry';
      const isShieldBearer = unit.unitType === 'shield';
      const isMage = unit.unitType === 'taoist';
      const isArcher = unit.unitType === 'archer';
      const isSwordsman = unit.unitType === 'sword';
      const isSpearman = unit.unitType === 'spear';

      if (isCavalry) {
        currentCavalry.add(entity);
      }
      if (isShieldBearer) {
        currentShieldBearers.add(entity);
      }
      if (isMage) {
        currentMages.add(entity);
      }
      if (isArcher) {
        currentArchers.add(entity);
      }
      if (isSwordsman) {
        currentSwordsmen.add(entity);
      }
      if (isSpearman) {
        currentSpearmen.add(entity);
      }

      // Handle cavalry separately if renderer is available
      if (isCavalry && this.cavalryRenderer) {
        if (!this.trackedCavalry.has(entity)) {
          const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
          const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

          this.cavalryRenderer.addCavalry(
            entity,
            position.x,
            position.y,
            position.z,
            faction.factionId as FactionId
          );
          this.trackedCavalry.add(entity);
        }
      }
      // Handle shield bearers separately if renderer is available
      else if (isShieldBearer && this.shieldBearerRenderer) {
        if (!this.trackedShieldBearers.has(entity)) {
          const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
          const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

          this.shieldBearerRenderer.addShieldBearer(
            entity,
            position.x,
            position.y,
            position.z,
            faction.factionId as FactionId
          );
          this.trackedShieldBearers.add(entity);
        }
      }
      // Handle mages separately if renderer is available
      else if (isMage && this.mageRenderer) {
        if (!this.trackedMages.has(entity)) {
          const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
          const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

          this.mageRenderer.addMage(
            entity,
            position.x,
            position.y,
            position.z,
            faction.factionId as FactionId
          );
          this.trackedMages.add(entity);
        }
      }
      // Handle archers separately if renderer is available
      else if (isArcher && this.archerRenderer) {
        if (!this.trackedArchers.has(entity)) {
          const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
          const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

          this.archerRenderer.addArcher(
            entity,
            position.x,
            position.y,
            position.z,
            faction.factionId as FactionId
          );
          this.trackedArchers.add(entity);
        }
      }
      // Handle swordsmen separately if renderer is available
      else if (isSwordsman && this.swordsmanRenderer) {
        if (!this.trackedSwordsmen.has(entity)) {
          const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
          const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

          this.swordsmanRenderer.addSwordsman(
            entity,
            position.x,
            position.y,
            position.z,
            faction.factionId as FactionId
          );
          this.trackedSwordsmen.add(entity);
        }
      }
      // Handle spearmen separately if renderer is available
      else if (isSpearman && this.spearmanRenderer) {
        if (!this.trackedSpearmen.has(entity)) {
          const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
          const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

          this.spearmanRenderer.addSpearman(
            entity,
            position.x,
            position.y,
            position.z,
            faction.factionId as FactionId
          );
          this.trackedSpearmen.add(entity);
        }
      } else if (!this.trackedUnits.has(entity)) {
        const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
        const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

        this.unitRenderer.addUnit(
          entity,
          unit.unitType as UnitType,
          position.x,
          position.y,
          position.z,
          faction.factionId as FactionId
        );
        this.trackedUnits.add(entity);
      }
    }

    // Update existing units
    for (const entity of units) {
      const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const unit = world.getComponent<UnitComponent>(entity, ComponentTypes.Unit)!;

      if (unit.unitType === 'cavalry' && this.cavalryRenderer) {
        this.cavalryRenderer.updateCavalry(entity, position.x, position.y, position.z);
      } else if (unit.unitType === 'shield' && this.shieldBearerRenderer) {
        this.shieldBearerRenderer.updateShieldBearer(entity, position.x, position.y, position.z);
      } else if (unit.unitType === 'taoist' && this.mageRenderer) {
        this.mageRenderer.updateMage(entity, position.x, position.y, position.z);
      } else if (unit.unitType === 'archer' && this.archerRenderer) {
        this.archerRenderer.updateArcher(entity, position.x, position.y, position.z);
      } else if (unit.unitType === 'sword' && this.swordsmanRenderer) {
        this.swordsmanRenderer.updateSwordsman(entity, position.x, position.y, position.z);
      } else if (unit.unitType === 'spear' && this.spearmanRenderer) {
        this.spearmanRenderer.updateSpearman(entity, position.x, position.y, position.z);
      } else {
        this.unitRenderer.updateUnit(entity, unit.unitType as UnitType, position.x, position.y, position.z);
      }
    }

    // Remove dead cavalry
    for (const entity of this.trackedCavalry) {
      if (!currentCavalry.has(entity)) {
        this.cavalryRenderer?.removeCavalry(entity);
        this.trackedCavalry.delete(entity);
      }
    }

    // Remove dead shield bearers
    for (const entity of this.trackedShieldBearers) {
      if (!currentShieldBearers.has(entity)) {
        this.shieldBearerRenderer?.removeShieldBearer(entity);
        this.trackedShieldBearers.delete(entity);
      }
    }

    // Remove dead mages
    for (const entity of this.trackedMages) {
      if (!currentMages.has(entity)) {
        this.mageRenderer?.removeMage(entity);
        this.trackedMages.delete(entity);
      }
    }

    // Remove dead archers
    for (const entity of this.trackedArchers) {
      if (!currentArchers.has(entity)) {
        this.archerRenderer?.removeArcher(entity);
        this.trackedArchers.delete(entity);
      }
    }

    // Remove dead swordsmen
    for (const entity of this.trackedSwordsmen) {
      if (!currentSwordsmen.has(entity)) {
        this.swordsmanRenderer?.removeSwordsman(entity);
        this.trackedSwordsmen.delete(entity);
      }
    }

    // Remove dead spearmen
    for (const entity of this.trackedSpearmen) {
      if (!currentSpearmen.has(entity)) {
        this.spearmanRenderer?.removeSpearman(entity);
        this.trackedSpearmen.delete(entity);
      }
    }

    // Remove dead units
    for (const entity of this.trackedUnits) {
      if (!currentUnits.has(entity)) {
        // Try to get unit type, but it might be gone
        const unit = world.getComponent<UnitComponent>(entity, ComponentTypes.Unit);
        if (unit) {
          this.unitRenderer.removeUnit(entity, unit.unitType as UnitType);
        }
        this.trackedUnits.delete(entity);
      }
    }

    // Sync buildings
    const buildings = world.query([ComponentTypes.Position, ComponentTypes.Faction, ComponentTypes.Building]);
    const currentBuildings = new Set(buildings);

    // Add new buildings
    for (const entity of buildings) {
      if (!this.trackedBuildings.has(entity)) {
        const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
        const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
        const building = world.getComponent<BuildingComponent>(entity, ComponentTypes.Building)!;

        this.buildingRenderer.addBuilding(
          entity,
          building.buildingType as BuildingType,
          position.x,
          position.y,
          position.z,
          faction.factionId as FactionId
        );
        this.trackedBuildings.add(entity);
      }
    }

    // Remove destroyed buildings
    for (const entity of this.trackedBuildings) {
      if (!currentBuildings.has(entity)) {
        this.buildingRenderer.removeBuilding(entity);
        this.trackedBuildings.delete(entity);
      }
    }

    // Update renderer
    this.unitRenderer.update();
  }

  clear(): void {
    this.trackedUnits.clear();
    this.trackedCavalry.clear();
    this.trackedShieldBearers.clear();
    this.trackedMages.clear();
    this.trackedArchers.clear();
    this.trackedSwordsmen.clear();
    this.trackedSpearmen.clear();
    this.trackedBuildings.clear();
    this.unitRenderer.clear();
    this.cavalryRenderer?.clear();
    this.shieldBearerRenderer?.clear();
    this.mageRenderer?.clear();
    this.archerRenderer?.clear();
    this.swordsmanRenderer?.clear();
    this.spearmanRenderer?.clear();
    this.buildingRenderer.clear();
  }
}
